/**
 * The coop's nest + clucks as a single rune-backed store, kept in sync with
 * Dropbox through the backend proxy and cached in IndexedDB.
 *
 * `hydrate()` fills it from the cache instantly on startup; `pull()` reconciles
 * with Dropbox (delta → batch-download → parse). Browsing, search and backlinks
 * all read from memory.
 */
import { ApiError } from './api';
import {
  allClucks,
  allExports,
  allPages,
  deleteClucks,
  deleteExports,
  deletePages,
  getSyncMeta,
  putCluck,
  putExports,
  putPages,
  setSyncMeta,
  wipeCoop,
} from './cache';
import { storageBatchDownload, storageCoops, storageDelta, storageSetCoop } from './storage';
import { classifyEntry } from './coop-paths';
import { Bm25 } from './bm25';
import { parseNestPage } from '../shared/frontmatter.js';
import { parseClucksFile } from '../shared/clucks.js';
import { TYPE_DIRS, type CluckEntry, type ExportFile, type NestPage, type PageType } from '../shared/coop-types.js';
import type { StorageEntry } from '../shared/api-types.js';

export type SyncStatus = 'idle' | 'syncing' | 'error';

const BATCH = 400; // backend caps batch-download at 500

class Nest {
  pages = $state<NestPage[]>([]);
  clucks = $state<CluckEntry[]>([]);
  exports = $state<ExportFile[]>([]);
  status = $state<SyncStatus>('idle');
  error = $state<string | null>(null);
  lastPulledAt = $state<number | null>(null);
  hydrated = $state(false);

  /** Coop-folder resolution (issue #39). */
  coops = $state<string[]>([]);
  coopPath = $state<string | null>(null);
  needsCoopPick = $state(false);

  #index: Bm25 | null = null;
  #indexedFrom: NestPage[] | null = null;

  get count(): number {
    return this.pages.length;
  }

  /** BM25 over the current pages, rebuilt when `pages` is replaced. */
  #ensureIndex(): Bm25 {
    if (this.#index && this.#indexedFrom === this.pages) return this.#index;
    this.#index = new Bm25(
      this.pages.map((p) => ({ id: p.slug, title: p.slug.replace(/-/g, ' '), body: p.body })),
    );
    this.#indexedFrom = this.pages;
    return this.#index;
  }

  #hydrateHits(ids: { id: string }[]): NestPage[] {
    const bySlug = new Map(this.pages.map((p) => [p.slug, p]));
    return ids.map((h) => bySlug.get(h.id)).filter((p): p is NestPage => p !== undefined);
  }

  /** Pages of one type, title order. */
  byType(type: PageType): NestPage[] {
    return this.pages.filter((p) => p.type === type).sort((a, b) => a.slug.localeCompare(b.slug));
  }

  get(slug: string): NestPage | undefined {
    return this.pages.find((p) => p.slug === slug);
  }

  /** Pages that link to `slug`. */
  backlinks(slug: string): NestPage[] {
    return this.pages
      .filter((p) => p.slug !== slug && p.links.includes(slug))
      .sort((a, b) => a.slug.localeCompare(b.slug));
  }

  /** Local BM25 search over the nest (for the Read screen). */
  search(query: string, limit = 40): NestPage[] {
    if (!query.trim()) return [];
    return this.#hydrateHits(this.#ensureIndex().search(query, limit));
  }

  /** The pages a peck question should be answered from, most relevant first. */
  retrieve(question: string, limit = 10): NestPage[] {
    return this.#hydrateHits(this.#ensureIndex().search(question, limit));
  }

  // --- lifecycle -----------------------------------------------------

  async hydrate(): Promise<void> {
    try {
      const [pages, clucks, exports, meta] = await Promise.all([allPages(), allClucks(), allExports(), getSyncMeta()]);
      this.pages = pages;
      this.clucks = clucks.flatMap((m) => m.entries).sort((a, b) => b.date.localeCompare(a.date));
      this.exports = exports;
      this.coopPath = meta.coopPath;
      this.lastPulledAt = meta.lastPulledAt;
    } catch {
      /* empty cache / private mode — pull() will populate */
    } finally {
      this.hydrated = true;
    }
  }

  /** Resolve which App-folder subfolder is the coop; may set `needsCoopPick`. */
  async #resolveCoop(): Promise<string | null> {
    const { current, coops } = await storageCoops();
    this.coops = coops;
    if (current) {
      this.needsCoopPick = false;
      return current;
    }
    if (coops.length === 1) {
      await storageSetCoop(coops[0]!);
      this.needsCoopPick = false;
      return coops[0]!;
    }
    this.needsCoopPick = true;
    return null;
  }

  async pickCoop(path: string): Promise<void> {
    await storageSetCoop(path);
    this.needsCoopPick = false;
    await wipeCoop();
    this.pages = [];
    this.clucks = [];
    this.exports = [];
    this.coopPath = path;
    await this.pull({ full: true });
  }

  /** Throw the cache away and pull the whole coop again (Settings → Rebuild). */
  async rebuild(): Promise<void> {
    if (this.status === 'syncing') return;
    await wipeCoop();
    this.pages = [];
    this.clucks = [];
    this.exports = [];
    this.#index = null;
    await this.pull({ full: true });
  }

  /** Reconcile the cache with Dropbox. */
  async pull(opts: { full?: boolean } = {}): Promise<void> {
    if (this.status === 'syncing') return;
    this.status = 'syncing';
    this.error = null;
    try {
      const coop = await this.#resolveCoop();
      if (coop === null) {
        this.status = 'idle';
        return; // waiting on the user to pick
      }

      const meta = await getSyncMeta();
      let full = opts.full ?? false;
      if (meta.coopPath !== coop) {
        await wipeCoop();
        this.pages = [];
        this.clucks = [];
        this.exports = [];
        full = true;
      }
      this.coopPath = coop;

      const { entries, cursor } = await storageDelta(full ? undefined : (meta.cursor ?? undefined));
      await this.#applyEntries(entries);

      const saved = await setSyncMeta({
        cursor,
        coopPath: coop,
        lastPulledAt: Date.now(),
      });
      this.lastPulledAt = saved.lastPulledAt;
      this.status = 'idle';
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : 'Sync failed. Pull to retry.';
      this.status = 'error';
    }
  }

  async #applyEntries(entries: StorageEntry[]): Promise<void> {
    const toFetch: string[] = [];
    const deletedPageSlugs: string[] = [];
    const deletedMonths: string[] = [];
    const deletedExports: string[] = [];
    const freshExports: ExportFile[] = [];
    let cluckTouched = false;

    for (const e of entries) {
      const c = classifyEntry(e);
      if (c.kind === 'ignore') continue;
      if (c.kind === 'cluck') cluckTouched = true;
      // Exports are metadata-only (no content download — they're binary files).
      if (c.kind === 'export') {
        if (c.op === 'delete') deletedExports.push(c.path);
        else {
          freshExports.push({
            path: c.path,
            name: c.path.slice('exports/'.length),
            size: e.size ?? 0,
            modified: e.clientModified ?? '',
            rev: e.rev,
          });
        }
        continue;
      }
      if (c.op === 'delete') {
        if (c.kind === 'page')
          deletedPageSlugs.push(c.path.split('/').pop()!.replace(/\.md$/i, ''));
        else deletedMonths.push(c.month);
      } else {
        toFetch.push(c.path);
      }
    }

    // deletions first
    await Promise.all([deletePages(deletedPageSlugs), deleteClucks(deletedMonths), deleteExports(deletedExports)]);
    if (deletedPageSlugs.length) {
      this.pages = this.pages.filter((p) => !deletedPageSlugs.includes(p.slug));
    }
    if (deletedExports.length) {
      this.exports = this.exports.filter((x) => !deletedExports.includes(x.path));
    }

    // downloads, chunked
    const freshPages: NestPage[] = [];
    for (let i = 0; i < toFetch.length; i += BATCH) {
      const { files } = await storageBatchDownload(toFetch.slice(i, i + BATCH));
      for (const f of files) {
        const c = classifyEntry({ path: f.path, tag: 'file' });
        if (c.kind === 'cluck') {
          await putCluck({ month: c.month, entries: parseClucksFile(f.content), rev: f.rev });
        } else if (c.kind === 'page') {
          freshPages.push(parseNestPage(f.path, f.content, f.rev));
        }
      }
    }

    if (freshPages.length) {
      await putPages(freshPages);
      const bySlug = new Map(freshPages.map((p) => [p.slug, p]));
      this.pages = [...this.pages.filter((p) => !bySlug.has(p.slug)), ...freshPages];
    }

    if (freshExports.length) {
      await putExports(freshExports);
      const byPath = new Map(freshExports.map((x) => [x.path, x]));
      this.exports = [...this.exports.filter((x) => !byPath.has(x.path)), ...freshExports];
    }

    if (cluckTouched) {
      const months = await allClucks();
      this.clucks = months.flatMap((m) => m.entries).sort((a, b) => b.date.localeCompare(a.date));
    }
  }
}

export const nest = new Nest();
export { TYPE_DIRS };
